import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useSubscriptionWithTier, SubscriptionDetails } from '@/hooks/useSubscription';
import { useAuth } from '@/lib/auth';

interface SubscriptionContextType {
  subscription: SubscriptionDetails | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refreshSubscription: () => void;
  isActive: boolean;
  tierName: string;
  current_period_end: string | undefined;
  cancel_at_period_end: boolean | undefined;
  stripe_price_id: string | null | undefined;
  status: string | null | undefined;
  stripe_customer_id: string | null | undefined;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  // Default fallback values in case of errors
  const fallbackValues = {
    subscription: null,
    isLoading: false,
    isError: true,
    error: new Error("Failed to load subscription data"),
    refreshSubscription: () => console.warn("Refresh unavailable due to error"),
    isActive: false,
    tierName: "Free",
  };

  try {
    const {
      subscription,
      isLoading,
      isError,
      error,
      refreshSubscription,
      isActive,
      tierName,
      current_period_end,
      cancel_at_period_end,
      stripe_price_id,
      status,
      stripe_customer_id
    } = useSubscriptionWithTier();

    // Stripe webhooks update the DB on subscription change. This 5-minute poll
    // is a safety net; refetchOnWindowFocus on the underlying query is the
    // real freshness mechanism.
    useEffect(() => {
      if (!user?.id) return;
      const interval = setInterval(refreshSubscription, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }, [user?.id, refreshSubscription]);

    return (
      <SubscriptionContext.Provider value={{
        subscription,
        isLoading,
        isError,
        error,
        refreshSubscription,
        isActive,
        tierName,
        current_period_end,
        cancel_at_period_end,
        stripe_price_id,
        status,
        stripe_customer_id
      }}>
        {children}
      </SubscriptionContext.Provider>
    );
  } catch (e) {
    console.error("Error in SubscriptionProvider:", e);

    // Return fallback values if useSubscriptionWithTier throws an error
    return (
      <SubscriptionContext.Provider value={{
        ...fallbackValues,
        current_period_end: undefined,
        cancel_at_period_end: undefined,
        stripe_price_id: null,
        status: null,
        stripe_customer_id: null
      }}>
        {children}
      </SubscriptionContext.Provider>
    );
  }
};

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
}; 